import React from 'react';
import ReactDOM from 'react-dom/client';
import GwSelect from './GwSelect';
import { INVENTORY_STATE } from '../../../app/enums';
import NumberFormatter from '../../../app/formatters/NumberFormatter';

/** @typedef {import('../../../app/models/InventoryProductModel').InventoryProductItem} InventoryProductItem */
/** @typedef {import('../../../app/models/InventoryModel').InventoryItem} InventoryItem */

/**
 * @typedef {object} InventoryState
 * @property {?InventoryItem} inventory - L'inventaire actuel ou null si non chargé.
 * @property {string} barcode - Code-barres saisi pour la recherche de produit.
 * @property {string} error - Message d'erreur actuel, s'il y en a un.
 * @property {string} search - Terme de recherche saisi par l'utilisateur.
 * @property {{[key: string]: {counted_quantity: string, depreciation: string, comment: string, reason: string}}} lineDrafts - Brouillons des lignes d'inventaire, indexés par identifiant de produit.
 */

/**
 * @typedef {object} InventoryProps
 * @property {string} inventoryId - Identifiant de l'inventaire
 * @property {string} csrf - Jeton CSRF pour les requêtes sécurisées
 */

class Inventory extends React.Component {
  /**
   * @param {InventoryProps} props
   */
  constructor(props) {
    super(props);
    this.state = /** @type {InventoryState} */({
      inventory: null,
      barcode: '',
      error: '',
      search: '',
      lineDrafts: {}
    });
    this.barcodeInput = React.createRef();
  }

  componentDidMount() {
    this.loadInventory();
  }

  /**
   * Lors de la sélection d'une option de produit dans le composant de sélection.
  * @param {{value: string}} option - Option sélectionnée
   */
  selectProductOption = option => {
    var productData;

    try {
      productData = JSON.parse(option.value);
    } catch (error) {
      productData = { productId: option.value };
    }

    if (productData.productId) {
      this.addProduct({
        productId: parseInt(productData.productId, 10),
        variationId: productData.variationId ? parseInt(productData.variationId, 10) : null
      }).catch(() => {});
    }
  };

  /**
   * Ajoute un produit à l'inventaire et re-donne le focus au champ de saisie du code-barres.
   * @param {object} productData - Données du produit à ajouter.
   * @param {boolean} [initWithCountedQuantity=false] - Indique si le produit doit être initialisé avec la quantité comptée.
   * @returns {Promise<void>} Résout lorsque le produit a été ajouté.
   */
  async addProduct(productData, initWithCountedQuantity = false) {
    if (!this.state.inventory || this.state.inventory.state >= 2) {
      return;
    }

    await this.runRequest(`/api/inventory/_add/${ this.props.inventoryId }/product?initWithCountedQuantity=${ initWithCountedQuantity }`, productData);
    await this.loadInventory();
    this.setState({ barcode: '' }, this.focusBarcode);
  }

  /**
   * Ajoute toutes les options disponibles avec une quantité comptée initialisée à zéro.
   * @returns {Promise<void>}
   */
  async addAllProducts() {
    if (!this.state.inventory || this.state.inventory.state !== INVENTORY_STATE.DRAFT.value || !window.confirm(`Ajouter tous les produits à l'inventaire avec une quantité comptée à 0 ?`)) {
      return;
    }

    try {
      await this.runRequest(`/api/inventory/_add/${ this.props.inventoryId }/all-products`, {});
      await this.loadInventory();
    } catch (error) {
      this.setState({ error: error instanceof Error ? error.message : `Impossible de préparer l'inventaire.` });
    }
  }

  /**
   * Permet de charger les informations de l'inventaire depuis l'API.
   * @returns {Promise<void>} Résout lorsque les informations de l'inventaire ont été chargées.
   */
  async loadInventory() {
    try {
      var response = await fetch(`/api/inventory/_search/${ this.props.inventoryId }`, {
        headers: { Authorization: 'gestiaweb' }
      });
      var result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Impossible de charger l’inventaire.');
      }

      this.setState({ inventory: result.item }, this.focusBarcode);
    } catch (error) {
      this.setState({ error: error instanceof Error ? error.message : `Impossible de charger l'inventaire.` });
    }
  }

  /**
   * Permet de mettre le focus sur le champ de saisie du code-barres si l'inventaire est en brouillon ou en cours
   */
  focusBarcode() {
    if (this.barcodeInput.current && this.state.inventory && this.state.inventory.state < 2) {
      this.barcodeInput.current.focus();
    }
  }

  /**
   * Scanne un produit en utilisant le code-barres saisi dans le champ correspondant.
   * @param {React.ChangeEvent} event - Événement de changement dans le champ permettant de scanner un code-barres.
   * @returns {Promise<void>} Résout lorsque le scan est terminé.
   */
  async scanProduct(event) {
    event.preventDefault();

    const barcode = this.state.barcode.trim();

    if (!barcode || !this.state.inventory || this.state.inventory.state >= 2) {
      return;
    }

    try {
      var response = await fetch(`/api/product/_search/barcode/${ encodeURIComponent(barcode) }`, {
        headers: { Authorization: 'gestiaweb' }
      });
      var result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Produit introuvable.');
      }

      await this.addProduct({ productId: result.item.id, variationId: result.item.variationId }, true);
    } catch (error) {
      this.setState({ error: error instanceof Error ? error.message : 'Produit introuvable.' });
    }
  }

  /**
   * Met à jour une ligne de produit dans l'inventaire.
   * @param {InventoryProductItem} inventoryProduct - Ligne de produit
   * @param {any} values - Liste des valeurs à modifier, objet des propriétés avec leur valeurs
   * @returns {Promise<void>} - Résout lorsque la mise à jour est terminée.
   */
  async updateInventoryProduct(inventoryProduct, values) {
    var updatedProduct = await this.runRequest(`/api/inventory/_update/product/${ inventoryProduct.id }`, values);
    var currentInventory = /** @type {InventoryItem} */(this.state.inventory);

    this.setState({
      inventory: {
        ...currentInventory,
        inventory_products: currentInventory.inventory_products.map(item => item.id === updatedProduct.id ? updatedProduct : item)
      }
    });

    await this.loadInventory();
  }

  /**
   * Supprime une ligne de produit de l'inventaire.
   * @param {InventoryProductItem} inventoryProduct - Ligne de produit à supprimer.
   * @returns {Promise<void>} Résout lorsque la suppression est terminée.
   */
  async deleteProduct(inventoryProduct) {
    var inventory = this.state.inventory;

    if (!inventory || inventory.state >= 2 || !window.confirm(`Supprimer ${ inventoryProduct.name } de l'inventaire ?`)) {
      return;
    }

    this.setState({ error: '' });

    try {
      var response = await fetch(`/api/inventory/_delete/${ inventoryProduct.id }`, {
        headers: {
          Authorization: 'gestiaweb'
        }
      });
      var result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Impossible de supprimer la ligne.');
      }

      await this.loadInventory();
    } catch (error) {
      this.setState({ error: error instanceof Error ? error.message : 'Impossible de supprimer la ligne.' });
    }
  }

  /**
   * Permet de valider l'inventaire.
   * @returns {Promise<void>} Résout lorsque la validation est terminée.
   */
  async validateInventory() {
    if (!this.canValidateInventory()) {
      this.setState({ error: `Toutes les lignes doivent être comptées avant de valider l'inventaire.` });
      return;
    }

    await this.runRequest(`/api/inventory/_validate/${ this.props.inventoryId }`, {});
    await this.loadInventory();
  }

  /**
   * Annule l'inventaire.
   * @returns {Promise<void>} Résout lorsque l'annulation est terminée.
   */
  async cancelInventory() {
    const inventory = this.state.inventory;

    if (!inventory || inventory.state >= 2 || !window.confirm(`Annuler l'inventaire ${ inventory.reference } ?`)) {
      return;
    }

    try {
      var response = await fetch(`/api/_changeboolean/inventory/${ this.props.inventoryId }/state/3`, {
        headers: { Authorization: 'gestiaweb' }
      });

      if (!response.ok) {
        throw new Error(`Impossible d'annuler l'inventaire.`);
      }

      await this.loadInventory();
    } catch (error) {
      this.setState({ error: error instanceof Error ? error.message : `Impossible d'annuler l'inventaire.` });
    }
  }

  /**
   * Raffraîchit le stock théorique des produits d'un inventaire.
   * @returns {Promise<void>} Résout lorsque le rafraîchissement est terminé.
   */
  async refreshTheoreticalStock() {
    const inventory = this.state.inventory;

    if (!inventory || inventory.state >= 2 || !window.confirm(`Raffraîchir le stock théorique pour l'inventaire ${ inventory.reference } ?`)) {
      return;
    }

    try {
      var response = await fetch(`/api/inventory/_refresh-theoretical-stock/${ this.props.inventoryId }`, {
        headers: { Authorization: 'gestiaweb' }
      });

      if (!response.ok) {
        throw new Error(`Impossible de raffraîchir le stock théorique pour l'inventaire.`);
      }

      await this.loadInventory();
    } catch (error) {
      this.setState({ error: error instanceof Error ? error.message : `Impossible de raffraîchir le stock théorique pour l'inventaire.` });
    }
  }

 /**
  * Prépare et exécute une requête POST vers l'API avec les données fournies.
  * @param {string} url - URL de l'api
  * @param {any} data - Donnée à envoyer à l'api
  * @returns {Promise<any>} Résultat de la requête à l'API.
  */
  async runRequest(url, data) {
    this.setState({ error: '' });

    try {
      var response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: 'gestiaweb',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...data, _csrf: this.props.csrf })
      });
      var result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Une erreur est survenue.');
      }

      return result.item;
    } catch (error) {
      this.setState({ error: error instanceof Error ? error.message : 'Une erreur est survenue.' });
      throw error;
    }
  }

  /**
   * Vérifie si l'inventaire peut être validé. Un inventaire peut être validé si son état est inférieur à 2, s'il contient des lignes de produits, et si toutes les lignes ont été comptées.
   * @returns {boolean} true si l'inventaire peut être validé, false sinon.
   */
  canValidateInventory() {
    const inventory = this.state.inventory;
    const inventoryProducts = inventory?.inventory_products || [];

    if (!inventory) {
      return false;
    }

    return inventory.state < 2 && inventoryProducts.length > 0 && inventoryProducts.every(item => item.state === 1);
  }

  /**
   * Permet de modifier un champ d'une ligne de produit dans l'inventaire.
  * @param {any} event - Événement de changement du champ
   * @param {InventoryProductItem} inventoryProduct - Ligne de produit
   * @param {'counted_quantity'|'depreciation'|'comment'|'reason'} field - Champ à modifier
   * @returns {void}
   */
  changeLineField(event, inventoryProduct, field) {
    const currentDraft = this.state.lineDrafts[inventoryProduct.id] || {
      counted_quantity: inventoryProduct.counted_quantity,
      depreciation: inventoryProduct.depreciation,
      comment: inventoryProduct.comment || '',
      reason: inventoryProduct.reason || ''
    };

    this.setState({
      lineDrafts: {
        ...this.state.lineDrafts,
        [inventoryProduct.id]: {
          ...currentDraft,
          [field]: event.target.value
        }
      }
    });
  }

  /**
   * Permet de sauvegarder une ligne de produit dans l'inventaire.
   * @param {InventoryProductItem} inventoryProduct - Ligne de produit
   * @returns {Promise<void>} Une promesse qui se résout lorsque la ligne est sauvegardée
   */
  async saveLine(inventoryProduct) {
    const inventory = this.state.inventory;

    if (inventory && inventory.state >= 2) {
      return;
    }

    const inventoryProductObj = this.state.lineDrafts[inventoryProduct.id] || {
      counted_quantity: inventoryProduct.counted_quantity,
      depreciation: inventoryProduct.depreciation,
      comment: inventoryProduct.comment || '',
      reason: inventoryProduct.reason || ''
    };

    try {
      await this.updateInventoryProduct(inventoryProduct, inventoryProductObj);
      const lineDrafts = { ...this.state.lineDrafts };
      delete lineDrafts[inventoryProduct.id];
      this.setState({ lineDrafts });
    } catch (error) {
      this.setState({ error: error instanceof Error ? error.message : 'Impossible de valider la ligne.' });
    }
  }

  /**
   * Permet de valider une ligne de produit dans l'inventaire.
   * @param {InventoryProductItem} inventoryProduct - Ligne de produit à valider
   * @returns {Promise<void>} Une promesse qui se résout lorsque la ligne est validée
   */
  async validateLine(inventoryProduct) {
    const inventory = this.state.inventory;

    if (inventory && inventory.state >= 2) {
      return;
    }

    try {
      await this.updateInventoryProduct(inventoryProduct, { state: 1 });
    } catch (error) {
      this.setState({ error: error instanceof Error ? error.message : 'Impossible de valider la ligne.' });
    }
  }

  /**
   * Rendu de l'état de l'inventaire sous forme de badge coloré.
   * @param {number} state - L'état courant de l'inventaire
   * @returns {React.ReactElement|undefined} L'élément représentant l'état de l'inventaire
   */
  renderInventoryState(state) {
    let render;
    switch (state) {
      case INVENTORY_STATE.DRAFT.value:
        render = (
          <div className="badge badge--warning">{INVENTORY_STATE.DRAFT.name}</div>
        );
        break;
      case INVENTORY_STATE.IN_PROGRESS.value:
        render = (
          <div className="badge badge--warning">{INVENTORY_STATE.IN_PROGRESS.name}</div>
        );
        break;
      case INVENTORY_STATE.CLOSED.value:
        render = (
          <div className="badge badge--success">{INVENTORY_STATE.CLOSED.name}</div>
        );
        break;
      case INVENTORY_STATE.CANCELLED.value:
        render = (
          <div className="badge badge--danger">{INVENTORY_STATE.CANCELLED.name}</div>
        );
        break;
    }

    return render;
  }

  /**
   * Rendu d'une ligne de produit dans le tableau de l'inventaire.
   * @param {InventoryProductItem} inventoryProduct - Ligne de produit
   * @returns {React.ReactElement} L'élément représentant la ligne de produit
   */
  renderProduct(inventoryProduct) {
    const inventory = /** @type {InventoryItem} */(this.state.inventory);
    const currentState = inventory.state;
    const isLocked = currentState >= 2;
    const lineDraft = this.state.lineDrafts[inventoryProduct.id];
    const countedValue = lineDraft ? lineDraft.counted_quantity : inventoryProduct.counted_quantity;
    const depreciationValue = lineDraft ? lineDraft.depreciation : inventoryProduct.depreciation;
    const commentValue = lineDraft ? lineDraft.comment : inventoryProduct.comment || '';
    const reasonValue = lineDraft ? lineDraft.reason : inventoryProduct.reason || '';
    const difference = inventoryProduct.difference;
    const isValidated = inventoryProduct.state === 1;
    const isReadOnly = isValidated || isLocked;

    return (
      <tr className="table__tr" key={inventoryProduct.id}>
        <td className="table__td">
          {inventoryProduct.name}
          {inventoryProduct.reference ? (
            <>
              <br />
              <small>(référence : {inventoryProduct.reference})</small>
            </>
          ) : null}
        </td>
        <td className="table__td">{inventoryProduct.theoretical_quantity}</td>
        <td className="table__td">{isReadOnly ? inventoryProduct.counted_quantity : <input aria-label={`Quantité comptée pour ${ inventoryProduct.name }`} className="input-group__input" type="number" min="0" step="1" value={countedValue} disabled={isLocked} onChange={event => this.changeLineField(event, inventoryProduct, 'counted_quantity')} />}</td>
        <td className="table__td">
          {difference === 0 && <div className="badge badge--success">0</div>}
          {difference < 0 && <div className="badge badge--danger">{difference}</div>}
          {difference > 0 && <div className="badge badge--warning">+{difference}</div>}
        </td>
        <td className="table__td">{NumberFormatter.formatNumber(inventoryProduct.gross_value)}</td>
        <td className="table__td">{isReadOnly ? NumberFormatter.formatNumber(inventoryProduct.depreciation, '%') : <input aria-label={`Dépréciation pour ${ inventoryProduct.name }`} className="input-group__input" type="number" min="0" max="100" step="1" value={depreciationValue} disabled={isLocked} onChange={event => this.changeLineField(event, inventoryProduct, 'depreciation')} />}</td>
        <td className="table__td">{NumberFormatter.formatNumber(inventoryProduct.net_value)}</td>
        <td className="table__td">{isReadOnly ? commentValue || '-' : <input aria-label={`Commentaire pour ${ inventoryProduct.name }`} className="input-group__input" type="text" value={commentValue} disabled={isLocked} onChange={event => this.changeLineField(event, inventoryProduct, 'comment')} />}</td>
        <td className="table__td">{isReadOnly ? reasonValue || '-' : <input aria-label={`Motif pour ${ inventoryProduct.name }`} className="input-group__input" type="text" value={reasonValue} disabled={isLocked} onChange={event => this.changeLineField(event, inventoryProduct, 'reason')} />}</td>
        {!isLocked && <td className="table__td">{isValidated ? <div className="badge badge--success">Validé</div> : <div className="badge badge--danger">Non validé</div>}</td>}
        {!isLocked && (
          <td className="table__td table__td-sticky--last">
            <div className="flexline flexline--align-hz flexline--gap-8">
              {!isValidated && <button className="btn btn--no-label" type="button" disabled={currentState > 1} data-tooltip="Valider la ligne" onClick={() => this.validateLine(inventoryProduct)}><span className="material-icons">check</span></button>}
              {!isValidated && <button className="btn btn--no-label" type="button" disabled={isLocked} data-tooltip="Enregistrer la ligne" onClick={() => this.saveLine(inventoryProduct)}><span className="material-icons">save</span></button>}
              {currentState < 2 && <button className="btn btn--no-label btn--danger" type="button" data-tooltip="Supprimer la ligne" onClick={() => this.deleteProduct(inventoryProduct)}><span className="material-icons">delete</span></button>}
            </div>
          </td>
        )}
      </tr>
    );
  }

  render() {
    if (!this.state.inventory) {
      return <div>{this.state.error || 'Inventaire introuvable.'}</div>;
    }

    const inventory = this.state.inventory;
    const products = inventory.inventory_products || [];
    const countedProducts = products.filter(item => item.state === 1).length;
    const currentState = inventory.state;
    const isLocked = currentState >= 2;

    return (
      <div style={{ display: 'grid', gridTemplateRows: 'auto 1fr', height: '100%', gap: '8px' }}>
        <div className="grid grid--has-2-child grid--8-gap">
          <div className="uiblock uiblock--no-header" style={{overflow: 'visible'}}>
            <div className="uiblock__content">
              <div className="flexline flexline--gap-8">
                <h4 className="title">Comptage de l'inventaire : {inventory.reference}</h4>
                {this.renderInventoryState(inventory.state)}
              </div>
              <div className="space--8"></div>
              {inventory.description ? (
                <>
                  <p className="paragraph paragraph--no-margin">{inventory.description}</p>
                  <div className="space--8" />
                </>
              ) : ''}
              {!isLocked && (
                <>
                  <div className="space--16"></div>
                  <div className="grid grid--has-2-child grid--8-gap">
                    <GwSelect apiUrl="/api/product/inventory/_search" inputPlaceholder="Rechercher un produit" required="true" onSelect={this.selectProductOption} />
                    <form className="form" onSubmit={this.scanProduct.bind(this)}>
                      <div className="input-group input-group--no-margin">
                        <input aria-label="Code-barres" className="input-group__input" ref={this.barcodeInput} value={this.state.barcode} autoFocus placeholder="Ajoutez votre code-barres ici" onChange={event => this.setState({ barcode: event.target.value })} />
                      </div>
                    </form>
                  </div>
                </>
              )}
              <div className="space--16"></div>
              <div className="flexline flexline--gap-8">
                {currentState === INVENTORY_STATE.DRAFT.value && <button className="btn" type="button" onClick={() => this.addAllProducts()}><span className="material-icons">playlist_add</span>Ajouter tous les produits</button>}
                {currentState === 1 && <button className="btn btn--main-color" type="button" disabled={!this.canValidateInventory()} title={this.canValidateInventory() ? `Valider l'inventaire` : `Toutes les lignes doivent être comptées`} onClick={() => this.validateInventory()}><span className="material-icons">check_circle</span>Valider l'inventaire</button>}
                {currentState < 2 && <button className="btn btn--danger" type="button" onClick={() => this.cancelInventory()}><span className="material-icons">cancel</span>Annuler l'inventaire</button>}
                {currentState < 2 && <button className="btn" type="button" onClick={() => this.refreshTheoreticalStock()}><span className="material-icons">refresh</span>Raffraîchir le stock théorique</button>}
              </div>
              {this.state.error && <div className="mosaic__badge mosaic__badge--danger">{this.state.error}</div>}
            </div>
          </div>
          <div className="uiblock uiblock--no-header">
            <div className="uiblock__content">
              <div className="flexline flexline--gap-16">
                <div className={countedProducts === products.length ? "uicircle uicircle--small uicircle--success" : "uicircle uicircle--small"}>
                  <div className="uicircle__number">{countedProducts} / {products.length}</div>
                  <div className="uicircle__title">Validés</div>
                </div>
                <div className={inventory.total_products_with_difference === 0 ? "uicircle uicircle--small uicircle--success" : "uicircle uicircle--small uicircle--small uicircle--danger"}>
                  <div className="uicircle__number">{inventory.total_products_with_difference}</div>
                  <div className="uicircle__title">Écart</div>
                </div>
                <div className="uicircle uicircle--small">
                  <div className="uicircle__number">{inventory.total_theoretical_quantity}</div>
                  <div className="uicircle__title">Théorique totale</div>
                </div>
                <div className="uicircle uicircle--small uicircle--success">
                  <div className="uicircle__number">{inventory.total_counted_quantity}</div>
                  <div className="uicircle__title">Comptée</div>
                </div>
                <div className="uicircle uicircle--small">
                  <div className="uicircle__number">{NumberFormatter.formatNumber(inventory.total_gross_value)}</div>
                  <div className="uicircle__title">valeur brute</div>
                </div>
                <div className="uicircle uicircle--small">
                  <div className="uicircle__number">{NumberFormatter.formatNumber(inventory.total_net_value)}</div>
                  <div className="uicircle__title">valeur nette</div>
                </div>
              </div>
                <div className="space--16"></div>
                <div className="flexline flexline--gap-8 flexline--align-right">
                  {currentState >= 1 && <a className="btn" href={`/_export/inventory?search[id]=${inventory.id}`}><span className="material-icons">download</span>Exporter l'inventaire</a>}
                  {currentState >= 1 && <a className="btn" href={`/_export/inventory-product?search[inventoryId]=${inventory.id}`}><span className="material-icons">download</span>Exporter les lignes de l'inventaire</a>}
                </div>
            </div>
          </div>
        </div>
        <div className="uiblock uiblock--no-header">
          <div className="uiblock__content" style={{ overflowX: 'auto', overflowY: 'auto', minHeight: 0 }}>
            <div className="table table--sticky" style={{height: '100%'}}>
              <table className="table__table">
                <thead>
                  <tr className="table__tr">
                    <th className="table__th table__th-sticky">Produit</th>
                    <th className="table__th table__th-sticky">
                      Théorique
                      <div className="table__th--description">La quantité théorique attendue pour ce produit</div>
                    </th>
                    <th className="table__th table__th-sticky">Compté</th>
                    <th className="table__th table__th-sticky">
                      Écart
                      <div className="table__th--description">La différence entre la quantité théorique et la quantité comptée</div>
                    </th>
                    <th className="table__th table__th-sticky">
                      Valeur brute
                      <div className="table__th--description">La valeur brute de ce produit avant dépréciation</div>
                    </th>
                    <th className="table__th table__th-sticky">
                      Dépréciation (%)
                      <div className="table__th--description">La dépréciation appliquée à ce produit en %</div>
                    </th>
                    <th className="table__th table__th-sticky">
                      Valeur nette
                      <div className="table__th--description">La valeur nette de ce produit après dépréciation</div>
                    </th>
                    <th className="table__th table__th-sticky">
                      Commentaire
                      <div className="table__th--description">Commentaire concernant le comptage</div>
                    </th>
                    <th className="table__th table__th-sticky">
                      Motif
                      <div className="table__th--description">Motif d'un éventuel écart</div>
                    </th>
                    {!isLocked && <th className="table__th table__th-sticky">État</th>}
                    {!isLocked && <th className="table__th table__th-sticky--last">Actions</th>}
                  </tr>
                </thead>
                <tbody>{products.map(this.renderProduct.bind(this))}</tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

var inventoryNode = document.getElementById('inventory');
if (inventoryNode) {
  var root = ReactDOM.createRoot(inventoryNode);
  root.render(<Inventory inventoryId={inventoryNode.dataset.id} csrf={inventoryNode.dataset.csrf} />);
}
